'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { getTranslations } from '@/lib/i18n';

export default function FrWeddingNav({ transparent = false }: { transparent?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const t = getTranslations('fr');

  const navLinks = [
    { label: t.nav.theDay, href: '/fr/schedule', key: 'nav-schedule' },
    { label: t.nav.theme, href: '/fr/theme', key: 'nav-theme' },
    { label: t.nav.rsvp, href: '/fr/rsvp', key: 'nav-rsvp' },
    { label: t.nav.accommodation, href: '/fr/dartmouth', key: 'nav-dartmouth' },
    { label: t.nav.gallery, href: '/photo-gallery', key: 'nav-gallery' },
    { label: t.nav.gift, href: '/fr/gift', key: 'nav-gift' },
    { label: t.nav.faq, href: '/fr/faq', key: 'nav-faq' },
  ];

  // Build EN equivalent path
  const enPath = (() => {
    if (pathname === '/fr/schedule') return '/wedding-day-schedule';
    if (pathname === '/fr/rsvp') return '/rsvp-page';
    if (pathname === '/fr/dartmouth') return '/places-to-stay-things-to-do';
    if (pathname === '/fr/gift') return '/honeymoon-fund';
    if (pathname === '/fr/faq') return '/faq';
    if (pathname === '/fr/theme') return '/theme';
    if (pathname === '/fr') return '/landing-page';
    return '/landing-page';
  })();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = transparent && !scrolled && !mobileOpen;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-600 ${
          isTransparent
            ? 'bg-transparent' :'bg-buttermilk/95 backdrop-blur-sm border-b border-border'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 h-16 flex items-center justify-between">
          <Link
            href="/fr"
            className="font-serif text-xl tracking-[0.15em] transition-opacity duration-300 hover:opacity-70 whitespace-nowrap"
            style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
          >
            G & B
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                href={link.href}
                className={`text-xs tracking-[0.25em] uppercase font-sans font-400 transition-all duration-300 ${
                  pathname === link.href
                    ? isTransparent
                      ? 'opacity-100 border-b border-current pb-0.5' :'opacity-100 border-b border-faded-navy pb-0.5' :'opacity-60 hover:opacity-100'
                }`}
                style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
              >
                {link.label}
              </Link>
            ))}
            {/* Language toggle */}
            <Link
              href={enPath}
              className="opacity-60 hover:opacity-100 transition-all duration-300 border border-current px-2 py-0.5 text-base leading-none"
              style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
              title="English"
            >
              🇬🇧
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden transition-opacity duration-300 hover:opacity-70"
            style={{ color: isTransparent ? '#FAF8F3' : '#2C3E50' }}
            aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 bg-buttermilk flex flex-col items-center justify-center transition-all duration-600 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="font-serif text-3xl tracking-[0.2em] text-faded-navy mb-16">G & B</div>
        <div className="flex flex-col items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={`mobile-${link.key}`}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className={`font-serif text-2xl tracking-[0.1em] text-faded-navy transition-opacity duration-300 text-center ${
                pathname === link.href ? 'opacity-100' : 'opacity-50 hover:opacity-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href={enPath}
            onClick={() => setMobileOpen(false)}
            className="text-2xl text-faded-navy opacity-50 hover:opacity-100 transition-opacity duration-300 border border-faded-navy/30 px-4 py-1"
            title="English"
          >
            🇬🇧
          </Link>
        </div>
        <div className="mt-20 text-xs tracking-[0.3em] uppercase font-sans text-stone text-center">
          XXIX · VIII · MMXXVI
        </div>
      </div>
    </>
  );
}
